import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { vendorSupabase as supabase } from '@/integrations/supabase/vendor-client';
import { getVendorSessionId, handleVendorDispatchResult } from '@/lib/vendorSession';

// Module-level cache of the last FCM token observed via the
// `'registration'` listener. Used by signOut() to scope its
// push_tokens deactivation to this device's row only.
let cachedFcmToken: string | null = null;

const LAST_REGISTERED_KEY = 'vendor_fcm_last_registered';

type LastRegistered = { vendorId: string; token: string };

export function getLastRegisteredFcm(): LastRegistered | null {
  try {
    const raw = localStorage.getItem(LAST_REGISTERED_KEY);
    return raw ? (JSON.parse(raw) as LastRegistered) : null;
  } catch {
    return null;
  }
}

export function setLastRegisteredFcm(value: LastRegistered | null) {
  try {
    if (value) localStorage.setItem(LAST_REGISTERED_KEY, JSON.stringify(value));
    else localStorage.removeItem(LAST_REGISTERED_KEY);
  } catch { /* ignore */ }
}

function isAlreadyRegistered(vendorId: string, token: string): boolean {
  const last = getLastRegisteredFcm();
  return !!last && last.vendorId === vendorId && last.token === token;
}

export function getCachedFcmToken(): string | null {
  return cachedFcmToken;
}

/**
 * Mint a brand-new FCM token synchronously for the caller (used by
 * `verifyOtp` BEFORE invoking `vendor-login`, so the new token is created
 * server-side at Verify OTP as the single authoritative point).
 *
 * Android-only. Retries up to 3 times with 1.5s backoff. Only calls
 * `FCM.deleteInstance()` when a prior `last_registered` marker exists —
 * on a brand-new install the delete+refresh round-trip frequently
 * arrives after the timeout window, leaving the caller with a null token.
 */
export async function mintFreshFcmToken(
  timeoutMs = 12000,
): Promise<{ token: string; platform: string } | null> {
  if (!Capacitor.isNativePlatform()) return null;
  const platform = Capacitor.getPlatform();
  if (platform !== 'android') return null;
  try {
    const { FCM } = await import('@capacitor-community/fcm');
    const hasPrior = !!getLastRegisteredFcm();
    const refreshPromise = (async () => {
      let lastErr: unknown = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          if (hasPrior && attempt === 0) {
            try { await FCM.deleteInstance(); } catch { /* non-fatal */ }
          }
          const { token } = await FCM.refreshToken();
          if (token) return token;
        } catch (err) {
          lastErr = err;
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
      if (lastErr) console.warn('[vendor-fcm] refreshToken retries exhausted', lastErr);
      return null;
    })();
    const token = await Promise.race<string | null>([
      refreshPromise,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
    ]);
    if (!token) return null;
    cachedFcmToken = token;
    return { token, platform };
  } catch (err) {
    console.warn('[vendor-fcm] mintFreshFcmToken failed', err);
    return null;
  }
}

export async function postRegisterFcmToken(vendorId: string, token: string) {
  const platform = Capacitor.getPlatform();
  try {
    const res = await supabase.functions.invoke('vendor-data', {
      body: {
        action: 'register_fcm_token',
        vendor_id: vendorId,
        fcm_token: token,
        platform,
        session_id: getVendorSessionId(),
      },
    });
    handleVendorDispatchResult(res);
    if (res?.data?.success === false) {
      console.warn('[vendor-fcm] register_fcm_token rejected', res?.data?.error);
    } else {
      setLastRegisteredFcm({ vendorId, token });
      console.log('[vendor-fcm] register_fcm_token ok');
    }
  } catch (err) {
    console.warn('[vendor-fcm] register_fcm_token failed', err);
  }
}

/**
 * Diff-gated + session-gated safety net for OS-driven token rotations.
 * Login-time token creation is owned EXCLUSIVELY by `vendor-login`
 * (Verify OTP). This hook never calls register() on resume or permission
 * change, and never posts a duplicate token.
 */
export function useVendorFcmRegistration(vendorId: string | null) {
  useEffect(() => {
    if (!vendorId || !Capacitor.isNativePlatform()) return;

    let cleanupListeners: Array<{ remove: () => Promise<void> }> = [];
    let cancelled = false;

    (async () => {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const { LocalNotifications } = await import('@capacitor/local-notifications');

        // Pre-create the high-importance `vendor-alerts` channel BEFORE
        // calling register(), so any FCM message received on cold start
        // can be displayed by the OS even before any other JS has run.
        try {
          await LocalNotifications.createChannel({
            id: 'vendor-alerts',
            name: 'Vendor Alerts',
            description: 'Vendor notifications',
            importance: 5,
            visibility: 1,
            vibration: true,
            lights: true,
          });
        } catch (err) {
          console.warn('[vendor-fcm] createChannel failed', err);
        }

        const regListener = await PushNotifications.addListener('registration', async (token) => {
          if (cancelled) return;
          cachedFcmToken = token.value;
          if (isAlreadyRegistered(vendorId, token.value)) {
            console.log('[vendor-fcm] token unchanged — skipping register_fcm_token');
            return;
          }
          const session = getVendorSessionId();
          if (!session) {
            console.log('[vendor-fcm] no active session — skipping rotation write');
            return;
          }
          console.log('[vendor-fcm] OS token rotated — posting to backend');
          await postRegisterFcmToken(vendorId, token.value);
        });

        const errListener = await PushNotifications.addListener('registrationError', (err) =>
          console.error('[vendor-fcm] registration error', err)
        );

        cleanupListeners.push(regListener, errListener);
        await PushNotifications.register();

        // Proactive post-login self-heal: if no `vendor_fcm_last_registered`
        // marker exists for this vendor (e.g. mint returned null on slow
        // Play Services start, or warm-app login where register() won't
        // re-fire), mint and POST a token row directly. Bounded retry.
        (async () => {
          for (let attempt = 0; attempt < 6 && !cancelled; attempt++) {
            const last = getLastRegisteredFcm();
            if (last && last.vendorId === vendorId) return;
            if (!getVendorSessionId()) {
              await new Promise((r) => setTimeout(r, 2000));
              continue;
            }
            const minted = await mintFreshFcmToken();
            if (minted?.token) {
              await postRegisterFcmToken(vendorId, minted.token);
              const after = getLastRegisteredFcm();
              if (after && after.vendorId === vendorId) return;
            }
            await new Promise((r) => setTimeout(r, 3000 + attempt * 1500));
          }
        })();
      } catch (err) {
        console.warn('[vendor-fcm] init failed', err);
      }
    })();

    return () => {
      cancelled = true;
      cleanupListeners.forEach((l) => {
        try { l.remove(); } catch { /* ignore */ }
      });
    };
  }, [vendorId]);
}
